import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ILocalization, LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UIPageStore } from 'stores/ui-page-store';

import { MessagingCardViewModel } from 'models';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

import { DoubleLink } from 'components/common/media/double-link';
import { KNOWN_ACTIONS, MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { MoreDetailsLx } from 'components/common/messaging-card/more-details-lx';
import { TermsAndConditionsDialog } from 'components/common/messaging-card/terms-and-conditions-dialog';

const LinkTracked = ClickTracker(DoubleLink);
export interface MessagingCardProps extends LocalizedComponentProps {
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  className?: string;
  liveAnnounceLevel?: string;
  model: MessagingCardViewModel;
  tag?: string | React.ComponentClass | React.FunctionComponent;
  index?: number;
  uiPage?: UIPageStore;
  testId?: string;
}

@withLocalization
@inject('analytics', 'uiPage', 'routeStore')
export class MessagingCard extends React.PureComponent<MessagingCardProps> {
  public static defaultProps: Partial<MessagingCardProps> = {
    className: 'uitk-cell xl-cell-1-3 l-cell-1-3 m-cell-1-2 s-cell-1-1',
    liveAnnounceLevel: 'polite',
    tag: 'li'
  };

  public render() {
    const { model, analytics, className: tagClassName, liveAnnounceLevel, tag: Tag, routeStore, uiPage, l10n, testId } = this.props;

    if (!model) {
      return null;
    }

    const titleModel = model.titleModel;
    const icon = titleModel && titleModel.icon;
    const cardClasses = 'messaging-card all-x-padding-three all-y-padding-three';
    const defaultWrapperProps = {
      testId,
      Tag,
      liveAnnounceLevel,
      tagClassName,
      cardClasses,
      withBorder: false
    };

    return (
      <MessagingCardWrapper {...defaultWrapperProps}>
        {icon && <UitkIconWrapper icon={icon} />}
        <MessagingCardBody analytics={analytics} model={model} routeStore={routeStore} uiPage={uiPage} l10n={l10n} />
      </MessagingCardWrapper>
    );
  }
}

export const UitkIconWrapper = ({ icon }) => (
  <AbacusExperiment name="MOD_eligible_properties_icon">
    <AbacusControl>
      <UitkIcon name={icon} size={UitkIconSize.MEDIUM} className="messaging-card__title-icon all-r-margin-one all-cell-align-middle" />
    </AbacusControl>
    <AbacusVariant bucket={1}>
      <UitkIcon name={icon} size={UitkIconSize.LARGE} className="messaging-card__title-icon all-r-margin-one all-cell-align-middle" />
    </AbacusVariant>
  </AbacusExperiment>
);

export const MessagingCardWrapper = ({
  Tag,
  liveAnnounceLevel,
  tagClassName,
  cardClasses,
  withBorder,
  children,
  testId = 'messaging-card'
}) => (
  <Tag className={tagClassName} aria-live={liveAnnounceLevel} data-stid={testId}>
    <UitkCard className={cardClasses} withBorder={withBorder}>
      <UitkCardContent>{children}</UitkCardContent>
    </UitkCard>
  </Tag>
);

interface MessagingCardBodyProps {
  model: MessagingCardViewModel;
  analytics?: AnalyticsStore;
  routeStore?: RouteStore;
  uiPage?: UIPageStore;
  l10n?: ILocalization;
}

const MessagingCardDialogs = ({ primaryAction, secondaryAction, routeStore }) => {
  if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.TERMS_AND_CONDITIONS
  ) {
    return <TermsAndConditionsDialog action={primaryAction} routeStore={routeStore} />;
  } else if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.FREE_LX
  ) {
    return <MoreDetailsLx action={primaryAction} routeStore={routeStore} />;
  }

  return null;
};

const MessagingCardBody: React.FunctionComponent<MessagingCardBodyProps> = ({ analytics, model, routeStore, uiPage, l10n }) => {
  const titleModel = model.titleModel;
  const subtitleModel = model.subtitleModel;
  const title = titleModel && titleModel.text;
  const subtitle = subtitleModel && subtitleModel.text;
  const actionModel = model.actionModel || {};
  const primaryAction = actionModel.primary || {};
  const secondaryAction = actionModel.secondary || {};
  const showNotification = () => {
    if (uiPage && l10n) {
      uiPage.showNotification(l10n.formatText('coupon.copy.notification'));
    }
  };

  return (
    <>
      <MessagingCardDialogs primaryAction={primaryAction} secondaryAction={secondaryAction} routeStore={routeStore} />
      <div className="uitk-cell all-cell-1-2 all-cell-fill">
        {title && <div className="messaging-card__title uitk-type-medium">{title}</div>}

        {subtitle && <div className="messaging-card__subtitle uitk-type-200">{subtitle}</div>}

        {primaryAction.text && (
          <div className="all-t-padding-two uitk-type-200">
            {secondaryAction.text && (
              <LinkTracked
                to={secondaryAction.href}
                rfrr={secondaryAction.rfrr}
                linkName="Secondary Messaging Action"
                trackImmediate={true}
                className="all-r-margin-six"
                onClick={MessagingCardActions.resolveAction(
                  routeStore,
                  secondaryAction,
                  analytics,
                  showNotification,
                  actionModel.primary.rfrr
                )}
              >
                {secondaryAction.text}
              </LinkTracked>
            )}

            {primaryAction.text && (
              <LinkTracked
                to={primaryAction.href}
                rfrr={primaryAction.rfrr}
                linkName="Primary Messaging Action"
                trackImmediate={true}
                onClick={MessagingCardActions.resolveAction(
                  routeStore,
                  primaryAction,
                  analytics,
                  showNotification,
                  actionModel.primary.rfrr
                )}
              >
                {primaryAction.text}
              </LinkTracked>
            )}
          </div>
        )}
      </div>
    </>
  );
};
MessagingCardBody.displayName = 'MessagingCardBody';
